import { Box, Container, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { FaDiscord } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { CardWall } from '../components/home/CardWall';
import { FeatureCarousel, type FeatureCarouselSlide } from '../components/home/FeatureCarousel';
import { RiftboundButton } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { AppPageHeader } from '../components/layout/AppPageHeader';
import { useCardWallPool } from '../hooks/useCardWallPool';
import { SITE_SEO_HOME_DESCRIPTION, SITE_SEO_HOME_TITLE, SITE_SEO_KEYWORDS } from '../constants/site';
import cardSearchScreen from '../images/feature-carousel/card-search.png';
import collectionStatsScreen from '../images/feature-carousel/collection-stats.png';
import deckBuilderScreen from '../images/feature-carousel/deck-builder.png';
import eventPosterScreen from '../images/feature-carousel/event-poster.png';
import proxyPrintScreen from '../images/feature-carousel/proxy-print.png';

/** Intrinsics from `public/riftbound-db-logo.png` (wordmark). */
const HOMEPAGE_WORDMARK_W = 1023;
const HOMEPAGE_WORDMARK_H = 189;

const HOMEPAGE_WORDMARK_URL = `${import.meta.env.BASE_URL}riftbound-db-logo.png`;

/** Descriptive alt + title for crawlers and assistive tech (keep in sync with homepage purpose). */
const HOMEPAGE_WORDMARK_ALT =
  'Riftbound DB wordmark: fan-built site to search cards in Riot’s Riftbound TCG, track your collection, build decks, print tabletop proxies, and keep score at game night.';
const HOMEPAGE_WORDMARK_TITLE =
  'Riftbound DB — Riftbound card database, deck builder, collection tracker, proxy prints, and match tracker';

/** Community Discord server (invite link). */
const DISCORD_INVITE_URL = 'https://discord.gg/HNYv6agPaM';

const HOME_FEATURES = [
  {
    eyebrow: 'Cards',
    title: 'Browse every Riftbound card.',
    body: 'Search the full card database by region, domain, champion, keyword, cost, might, rarity, and set.',
    mockTitle: 'Card search',
    mockDetail: 'Filter by region, domain, type, cost, rarity, and keywords.',
    domain: 'Order',
    imageSrc: cardSearchScreen,
    imageAlt: 'Riftbound DB mobile card search showing a grid of Riftbound cards and filter controls.',
    ctaLabel: 'Search cards',
    ctaTo: '/search',
  },
  {
    eyebrow: 'Collection',
    title: 'Know exactly what you own.',
    body: 'Track copy counts, spot missing playsets, scan physical cards, and export your Riftbound collection.',
    mockTitle: 'Collection tracker',
    mockDetail: 'Owned copies, missing cards, scanner, CSV, and Excel export.',
    domain: 'Calm',
    imageSrc: collectionStatsScreen,
    imageAlt: 'Riftbound DB collection statistics screen showing owned cards, rarity counts, and set progress.',
    ctaLabel: 'Open collection',
    ctaTo: '/collection',
  },
  {
    eyebrow: 'Decks',
    title: 'Build, validate, and publish decks.',
    body: 'Create legal lists, tune sideboards, write guides, export decks, and browse community decklists.',
    mockTitle: 'Deck builder',
    mockDetail: 'Legend, champion, runes, main deck, sideboard, guide, and validation.',
    domain: 'Mind',
    imageSrc: deckBuilderScreen,
    imageAlt: 'Riftbound DB deck builder screen showing deck validation, card search, and deck list controls.',
    ctaLabel: 'Browse decks',
    ctaTo: '/decks',
  },
  {
    eyebrow: 'Play',
    title: 'Run the night. Share the recap.',
    body: 'Keep score at the table, run Nexus Night or Swiss, let a guest join, and share a recap poster when the night ends.',
    mockTitle: 'Event recap',
    mockDetail: 'Live score, guest join, Nexus Night rounds, and a shareable poster.',
    domain: 'Fury',
    imageSrc: eventPosterScreen,
    imageAlt: 'Riftbound DB Nexus Night recap with an Ornn poster, round-by-round scores, and a 1-2 event record.',
    ctaLabel: 'Start tracking a match',
    ctaTo: '/play',
  },
  {
    eyebrow: 'Proxy',
    title: 'Print decks before buying cards.',
    body: 'Generate A4 proxy sheets for casual playtesting with cut marks, bleed, and PDF export.',
    mockTitle: 'Proxy printer',
    mockDetail: 'Tray, layout controls, cut marks, bleed, and print-ready PDFs.',
    domain: 'Chaos',
    imageSrc: proxyPrintScreen,
    imageAlt: 'Riftbound DB proxy printer screen showing a PDF preview and download controls.',
    ctaLabel: 'Print proxies',
    ctaTo: '/card-print',
  },
] as const satisfies readonly FeatureCarouselSlide[];

export function HomePage() {
  const sphereCards = useCardWallPool();

  return (
    <Box position="relative" w="full" overflow="visible">
      <SEO
        title={SITE_SEO_HOME_TITLE}
        description={SITE_SEO_HOME_DESCRIPTION}
        keywords={SITE_SEO_KEYWORDS}
        url="/"
        siteSchema
      />

      {sphereCards.length > 0 && (
        <Box
          aria-hidden
          position="absolute"
          top={{ base: 0, md: 0 }}
          left="50%"
          w="100vw"
          ml="-50vw"
          h={{ base: '560px', md: '720px' }}
          overflow="hidden"
          pointerEvents="none"
          zIndex={0}
        >
          <CardWall cards={sphereCards} />
        </Box>
      )}

      <Container
        maxW="1120px"
        position="relative"
        zIndex={1}
        px={{ base: 4, md: 6 }}
        py={{ base: 8, md: 10 }}
      >
        <VStack gap={{ base: 5, md: 6 }} align="center" textAlign="center" w="100%">
          <AppPageHeader
            pt={{ base: 32, md: 52 }}
            align="center"
            mb={0}
            title={
              <Box m={0} p={0} w="100%" maxW="min(920px, 100%)">
                <HomeHeroWordmark />
              </Box>
            }
          />

          <VStack gap={3} align="center" w="100%" pt={{ base: 1, md: 2 }}>
            <Heading as="h1" size={{ base: 'xl', md: '4xl' }} letterSpacing="tight" lineHeight="snug" textWrap="pretty">
              Search, scan, and collect every Riftbound card.
              <br />
              Build decks and print proxies for real-world games.
            </Heading>
            <Text
              color="textMuted"
              fontSize={{ base: 'md', md: 'xl' }}
              lineHeight="tall"
              maxW="640px"
              textWrap="pretty"
            >
              Then keep score at the table, run the event, and share the recap.
            </Text>
          </VStack>

          <HStack gap={3} flexWrap="wrap" justify="center">
            <RiftboundButton
              as={RouterLink}
              {...{ to: '/search' } as Record<string, unknown>}
              treatment="primary"
              size="lg"
            >
              Search cards
            </RiftboundButton>
            <RiftboundButton
              as={RouterLink}
              {...{ to: '/play' } as Record<string, unknown>}
              treatment="secondary"
              size="lg"
            >
              Track a match
            </RiftboundButton>
          </HStack>

          <FeatureCarousel
            heading="Everything you need for Riftbound night."
            headingId="home-feature-highlights"
            slides={HOME_FEATURES}
            controlsLabel="Homepage feature carousel controls"
          />

          <RiftboundButton
            as="a"
            {...{
              href: DISCORD_INVITE_URL,
              target: '_blank',
              rel: 'noopener noreferrer',
            } as Record<string, unknown>}
            treatment="secondary"
            size="lg"
          >
            <FaDiscord size={18} aria-hidden />
            Join our Discord
          </RiftboundButton>
        </VStack>
      </Container>
    </Box>
  );
}

/**
 * Brand-yellow wordmark (accent + PNG mask). A real `<img>` stays in the DOM for SEO/AT
 * (clip-offscreen technique); visible gold comes from `bg="accent"` + mask.
 */
function HomeHeroWordmark() {
  const maskCss = {
    WebkitMaskImage: `url(${HOMEPAGE_WORDMARK_URL})`,
    maskImage: `url(${HOMEPAGE_WORDMARK_URL})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
  } as const;

  return (
    <Box
      as="figure"
      position="relative"
      mx="auto"
      mb={0}
      w={{ base: 'min(94vw, 420px)', sm: 'min(88%, 480px)', md: 'min(72%, 560px)' }}
      maxW="100%"
      lineHeight={0}
    >
      <img
        src={HOMEPAGE_WORDMARK_URL}
        alt={HOMEPAGE_WORDMARK_ALT}
        title={HOMEPAGE_WORDMARK_TITLE}
        width={HOMEPAGE_WORDMARK_W}
        height={HOMEPAGE_WORDMARK_H}
        decoding="async"
        fetchPriority="high"
        loading="eager"
        draggable={false}
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      <Box
        aria-hidden
        mx="auto"
        h={{ base: '44px', sm: '54px', md: '76px', lg: '88px' }}
        aspectRatio={HOMEPAGE_WORDMARK_W / HOMEPAGE_WORDMARK_H}
        maxW="100%"
        bg="accent"
        flexShrink={0}
        css={{
          ...maskCss,
          filter:
            'drop-shadow(0 2px 20px rgba(0,0,0,0.55)) drop-shadow(0 1px 4px rgba(0,0,0,0.85))',
        }}
      />
    </Box>
  );
}

